import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackNavigation } from '@/hooks/use-back-navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Settings, Save, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authenticatedFetch } from '@/lib/api';

interface SettingItem {
  id: number;
  Name: string;
  Value: string;
}

interface Company {
  Id: number;
  Name: string;
  LandPhone: string;
  Email: string;
  GSTN: string;
  Mobile: string;
  CreatedOn: string;
  ContactPerson: string;
  ContactPersonNumber: string;
  Active: boolean;
  LogoPath: string;
  BankAccountHolder: string | null;
  BankName: string | null;
  BankAccountNo: string | null;
  BankIFSC: string | null;
  BankAddress: string | null;
  GST: number;
  Logo: string;
  secondline: string | null;
  id1: number;
  Address1: string;
  Address2: string;
  Address3: string;
  City: string;
  State: string;
  PinZip: string;
  Country: string;
  id2: number;
  CompanyID: number;
  AddressID: number;
  Active1: boolean;
  StartDate: string;
  EndDate: string;
}

interface CompaniesResponse {
  Header: any[];
  EditableColumns: any[];
  ValueChangeColumns: any[];
  TwoFields: any[];
  status: number;
  message: string;
  data: Company[];
  TotalRecords: number;
}

interface SettingsResponse {
  Header: any[];
  EditableColumns: any[];
  ValueChangeColumns: any[];
  TwoFields: any[];
  status: number;
  message: string;
  data: SettingItem[];
  TotalRecords: number;
}

interface SettingsGroup {
  title: string;
  description: string;
  icon: React.ReactNode;
  settings: string[];
}

// Communication Settings card hidden until functionality is built
const SETTINGS_GROUPS: Omit<SettingsGroup, 'icon'>[] = [
  // {
  //   title: "Communication Settings",
  //   description: "SMS, WhatsApp and messaging configuration",
  //   settings: [
  //     "SMS URL",
  //     "Whatsapp Key",
  //     "isWhatsapp",
  //     "Send msg with Invoice",
  //     "Sales Invoice SMS Template"
  //   ]
  // },
  {
    title: "Invoice Settings",
    description: "Invoice behavior and printing options",
    settings: [
      "Invoice Partner Required",
      "Invoice Qty Popup Required",
      "Invoice Default Payment Type",
      "Invoice No of Day for Due",
      "Refunds CN No of Days",
      "Invoice Print Template V2",
      "Invoice Template Full Page",
      "Image in Invoice",
      "Print Location Address",
      "Invoice Price Change",
      "Unknown User Ticketed",
      "Split Payment",
      "Serial Text",
      "FIFO Invoice Order",
      "Duplicate Invoice Line Items",
      "isCostPrice"
    ]
  },
  {
    title: "Product Settings",
    description: "Product configuration and barcode settings",
    settings: [
      "Products - isService Ticked",
      "Products - isTrackable Ticked",
      // "Products - WordSearch",        // hidden until functionality built
      "Products - Barcode Length",
      // "Default Barcode Type",         // hidden until functionality built
      // "Part word search",             // hidden until functionality built
      // "Open Barcode",                 // hidden until functionality built
      "isGender",
      "Img in Inventory",
      // "Auto Accept Inventory",        // hidden until functionality built
      "Expiry forever"
    ]
  },
  {
    title: "Printer & Thermal Settings",
    description: "Printing and thermal printer configuration",
    settings: [
      "Thermal Print Font",
      "Title Font Size",
      "Header Font Size",
      "Content Font Size",
      "KOT Enabled",
      "KOT",
      "Serial Print"
    ]
  },
  {
    title: "Finance & Points",
    description: "Financial settings and loyalty points",
    settings: [
      "DefaultGSTPer",
      "Same as GST",
      "Profit Margin",
      "Profit Margin Type",
      "Points Convertion",
      "Min Points for Redeem",
      "Rep Commission"
    ]
  },
  {
    title: "UPI & Payment",
    description: "UPI payment configuration",
    settings: [
      "UPIID",
      "UPINAME",
      "UPIQRSIZE",
      "UPI in Invoice"
    ]
  },
  {
    title: "Company & Misc",
    description: "Company settings and miscellaneous options",
    settings: [
      "Primary Company ID",
      "Location Name",
      "Backup Folder",
      "DOB Required"
    ]
  }
];

const FONT_OPTIONS = [
  "8-PM.ttf",
  "Arial",
  "Times New Roman",
  "Courier New",
  "Verdana"
];

const TEMPLATE_OPTIONS = [
  { value: "0", label: "Template 0 (Default)" },
  { value: "1", label: "Template 1" },
  { value: "2", label: "Template 2" },
  { value: "3", label: "Template 3" },
  { value: "4", label: "Template 4" }
];

// Invoice Print Template: display labels, API receives 0, 1, 2
const INVOICE_PRINT_TEMPLATE_OPTIONS = [
  { value: "0", label: "2inch print" },
  { value: "1", label: "3inch print" },
  { value: "2", label: "Multi Page Modern" }
];

const PAYMENT_TYPE_OPTIONS = [
  { value: "Cash", label: "Cash" },
  { value: "Card", label: "Card" },
  { value: "Wallet", label: "Wallet" },
  { value: "Coupon", label: "Coupon" },
  { value: "Cheque", label: "Cheque" },
  { value: "Due", label: "Due" },
  { value: "Points", label: "Points" }
];

// API expects unit as D (Days), W (Weeks), M (Months). Value sent as "number:unit" e.g. "2:D", "10:W"
const TIME_UNIT_OPTIONS = [
  { value: "D", label: "Days" },
  { value: "W", label: "Weeks" },
  { value: "M", label: "Months" }
];

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<SettingItem[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});

  // Load settings and companies from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load settings
        const settingsResponse = await authenticatedFetch<SettingsResponse>('/api/Settings?Count=100');
        
        if (settingsResponse.data && settingsResponse.data.data) {
          const settingsData = settingsResponse.data.data;
          setSettings(settingsData);
          
          // Initialize form data
          const initialFormData: Record<string, string> = {};
          settingsData.forEach(setting => {
            initialFormData[setting.Name] = setting.Value;
          });
          setFormData(initialFormData);
        }
        
        // Load companies
        const companiesResponse = await authenticatedFetch<CompaniesResponse>('/api/Companies');
        
        if (companiesResponse.data && companiesResponse.data.data) {
          setCompanies(companiesResponse.data.data);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load settings or companies');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Check if form data has changes
  useEffect(() => {
    const hasUnsavedChanges = settings.some(setting => 
      formData[setting.Name] !== setting.Value
    );
    setHasChanges(hasUnsavedChanges);
  }, [formData, settings]);

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBackToDashboard = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to exit?')) {
        navigate('/?tab=administrator');
      }
    } else {
      navigate('/?tab=administrator');
    }
  };

  // Back button (hardware/browser) should return to Administrator tab, not Reports
  useBackNavigation({
    onBack: handleBackToDashboard,
    showExitConfirmation: false,
  });

  const handleSave = async () => {
    try {
      setSaving(true);

      // Build list of changed settings: only id and Value. Toggles: "0"/"1". Number+unit fields (e.g. Invoice No of Day for Due): "number:unit" with D=Days, W=Weeks, M=Months.
      const changedSettings = settings
        .filter(setting => formData[setting.Name] !== undefined && formData[setting.Name] !== setting.Value)
        .map(setting => ({
          id: typeof setting.id === 'number' ? Math.round(setting.id) : Number(setting.id),
          Value: String(formData[setting.Name] ?? setting.Value),
        }));

      if (changedSettings.length === 0) {
        setHasChanges(false);
        return;
      }

      const response = await authenticatedFetch<{ status: number; message?: string; data: unknown }>('/api/UpdateSettings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: changedSettings }),
      });

      if (response.data?.status !== 0) {
        throw new Error(response.data?.message ?? 'Save failed');
      }

      // Update local state: merge new values into full settings list
      const updatedSettings = settings.map(setting => {
        const newValue = formData[setting.Name];
        if (newValue !== undefined) {
          return { ...setting, Value: newValue };
        }
        return setting;
      });
      setSettings(updatedSettings);
      setFormData(prev => {
        const next = { ...prev };
        updatedSettings.forEach(s => { next[s.Name] = s.Value; });
        return next;
      });
      setHasChanges(false);
      const successMessage = response.data?.message ?? 'Settings updated successfully.';
      toast.success(successMessage);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExit = () => {
    handleBackToDashboard();
  };

  const renderInput = (setting: SettingItem) => {
    const value = formData[setting.Name] || '';
    const name = setting.Name;

    // Invoice Print Template V2: dropdown (0=2inch, 1=3inch, 2=A4) - value sent to UpdateSettings API
    if (name === "Invoice Print Template V2") {
      return (
        <Select value={value} onValueChange={(newValue) => handleInputChange(name, newValue)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select template" />
          </SelectTrigger>
          <SelectContent>
            {INVOICE_PRINT_TEMPLATE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Toggle switch for 0/1 values
    if (value === "0" || value === "1") {
      return (
        <div className="flex items-center space-x-2">
          <Switch
            checked={value === "1"}
            onCheckedChange={(checked) => handleInputChange(name, checked ? "1" : "0")}
          />
          <span className="text-sm text-gray-600">
            {value === "1" ? "Enabled" : "Disabled"}
          </span>
        </div>
      );
    }

    // Number input for numeric values
    if (/^\d+$/.test(value) && !name.includes("ID")) {
      return (
        <Input
          type="number"
          value={value}
          onChange={(e) => handleInputChange(name, e.target.value)}
          className="w-full"
        />
      );
    }

    // Textarea for long text (SMS templates)
    if (name.toLowerCase().includes('template') || name.toLowerCase().includes('key')) {
      return (
        <Textarea
          value={value}
          onChange={(e) => handleInputChange(name, e.target.value)}
          className="w-full min-h-[80px]"
          placeholder={name.toLowerCase().includes('template') ? 'Enter template text...' : 'Enter key...'}
        />
      );
    }

    // Dropdown for font/template selections
    if (name.toLowerCase().includes('font') || name.toLowerCase().includes('template')) {
      const options = name.toLowerCase().includes('font') ? FONT_OPTIONS : TEMPLATE_OPTIONS;
      
      return (
        <Select value={value} onValueChange={(newValue) => handleInputChange(name, newValue)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={`Select ${name.toLowerCase().includes('font') ? 'font' : 'template'}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={typeof option === 'string' ? option : option.value} value={typeof option === 'string' ? option : option.value}>
                {typeof option === 'string' ? option : option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Dropdown for payment type
    if (name.toLowerCase().includes('payment type')) {
      return (
        <Select value={value} onValueChange={(newValue) => handleInputChange(name, newValue)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select payment type" />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Invoice No of Day for Due, Refunds CN No of Days: textbox + unit dropdown. Stored and sent as "number:unit" (e.g. "2:D", "10:W")
    if (name.toLowerCase().includes('day for due') || name.toLowerCase().includes('no of day')) {
      const [number, unit] = value.split(':');
      
      return (
        <div className="flex space-x-2">
          <Input
            type="number"
            value={number || ''}
            onChange={(e) => handleInputChange(name, `${e.target.value}:${unit || 'D'}`)}
            className="flex-1"
            placeholder="Number"
          />
          <Select value={unit || 'D'} onValueChange={(newUnit) => handleInputChange(name, `${number || '1'}:${newUnit}`)}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_UNIT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Dropdown for Company selection
    if (name === "Primary Company ID") {
      const selectedCompanyId = value ? parseInt(value) : undefined;
      
      return (
        <Select
          value={selectedCompanyId ? selectedCompanyId.toString() : ''}
          onValueChange={(newValue) => handleInputChange(name, newValue)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Company" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((company) => (
              <SelectItem key={company.Id} value={company.Id.toString()}>
                {company.Name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Default text input
    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => handleInputChange(name, e.target.value)}
        className="w-full"
        placeholder={`Enter ${name.toLowerCase()}...`}
      />
    );
  };

  const getSettingsForGroup = (group: Omit<SettingsGroup, 'icon'>) => {
    return settings.filter(setting => group.settings.includes(setting.Name));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToDashboard}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Administrator
              </Button>
            </div>
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
            </div>
            <div className="w-32" /> {/* Spacer for center alignment */}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        <div className="mb-8">
          <p className="text-gray-600">
            Configure system settings and preferences. Changes are saved automatically.
          </p>
        </div>

        {/* Settings Groups */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {SETTINGS_GROUPS.map((group, index) => {
            const groupSettings = getSettingsForGroup(group);
            if (groupSettings.length === 0) return null;

            return (
              <Card key={index} className="bg-white border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                      <Settings className="w-4 h-4" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-gray-900">
                        {group.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-gray-500">
                        {group.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <Accordion type="multiple" className="w-full" key={`accordion-${index}`}>
                    <AccordionItem value={`settings-group-${index}-${group.title.replace(/\s+/g, '-').toLowerCase()}`} className="border-0">
                      <AccordionTrigger className="text-sm font-medium text-gray-700 hover:text-gray-900 py-2">
                        {groupSettings.length} settings
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-4">
                        {groupSettings.map((setting) => (
                          <div key={setting.id} className="space-y-2">
                            <Label htmlFor={setting.Name} className="text-sm font-medium text-gray-700">
                              {setting.Name === "Primary Company ID"
                                ? "Company"
                                : setting.Name === "isGender"
                                  ? "Gender In Products"
                                  : setting.Name === "Invoice Print Template V2"
                                    ? "Invoice Print Template"
                                    : setting.Name === "isCostPrice"
                                      ? "Show Cost Price in Invoice"
                                      : setting.Name}
                            </Label>
                            {renderInput(setting)}
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>

      {/* Action Buttons */}
      <div className="fixed bottom-0 right-0 left-0 bg-white border-t border-gray-200 shadow-lg p-4 z-30">
        <div className="max-w-7xl mx-auto flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={handleExit}
            disabled={saving}
            className="text-gray-700 border-gray-300 hover:bg-gray-50"
          >
            <X className="w-4 h-4 mr-2" />
            Exit
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
